// Transaction types, and what each one means for money direction.
//
//   expense    money out — a purchase, on any account
//   income     money in  — a deposit into a cash account
//   repayment  a credit-card payment: the card-side leg of a transfer between
//              two of the user's own accounts, not spending
//   refund     money handed back by a merchant — a purchase reversed
//
// Refunds used to be typed 'repayment', because on a credit card both arrive as
// the same raw shape: a positive amount posted to the card. They behave nothing
// alike. A payment moves the user's own money and cancels against its cash leg;
// a refund cancels against the PURCHASE it reverses.
//
// Typing a return as a payment got four things wrong at once: the money came
// back but the category still read as spent (repayments are dropped from the
// budget lens), the row was force-categorized "Credit Cards" instead of keeping
// the merchant's category, the forecast expected a cash debit that was never
// coming (an unpaired card "payment" reads as a debit still in flight), and the
// next card payment was sized as if the returned item were still on the
// statement.

const MONEY_IN = new Set(['income', 'refund']);
// Types that belong to the spending lens — what a budget is measured against.
// A refund is in it as a credit; a card payment is not spending at all.
const SPENDING = new Set(['expense', 'refund']);

/** Does this type move money toward the user? */
export function isMoneyIn(type) {
  return MONEY_IN.has(type);
}

// Both the stored shape (`amount`) and the display shape produced by
// toDisplayTx (`amt`) reach these helpers.
function amountOf(tx) {
  return Number(tx?.amount ?? tx?.amt) || 0;
}

/** Amount signed by direction: positive is money in, negative is money out. */
export function signedAmount(tx) {
  const amount = amountOf(tx);
  return isMoneyIn(tx?.type) ? amount : -amount;
}

/** Is this type spending (or a credit against spending)? */
export function isSpendingType(type) {
  return SPENDING.has(type);
}

/**
 * What this transaction adds to "spent" — a purchase adds, a refund gives back,
 * anything else (income, a card payment) is not spending at all. Summing this
 * over a category or a month is what keeps a return from leaving the budget
 * consumed by a purchase the user no longer has.
 */
export function spendDelta(tx) {
  const amount = amountOf(tx);
  if (tx?.type === 'expense') return amount;
  if (tx?.type === 'refund') return -amount;
  return 0;
}

// Card issuers write payments in a small, stereotyped vocabulary — "PAYMENT
// THANK YOU", "ONLINE PAYMENT", "AUTOPAY", "DIRECTPAY FULL BALANCE", "EPAY".
// A merchant refund posts under the merchant's own name, so it matches none of
// them. This is what separates the two at sync time, before there is any
// cash-side leg to pair against.
const CARD_PAYMENT_PATTERNS = [
  /\bpay(?:ment|ments)?\b/,
  /\bp[ay]?y?mnt\b/,
  /\bpmt\b/,
  /\bpymt\b/,
  /\bautopay\b/,
  /\bauto[\s-]?pay\b/,
  /\bdirect[\s-]?pay\b/,
  /\be[\s-]?pay\b/,
  /\bbill[\s-]?pay\b/,
  /\bthank\s?you\b/,
];

/** Does this descriptor read as a credit-card payment rather than a refund? */
export function looksLikeCardPayment(description) {
  const text = String(description || '').toLowerCase();
  if (!text) return false;
  return CARD_PAYMENT_PATTERNS.some((re) => re.test(text));
}
